<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-needle-syringe me-2"></i>{{ $t('page.anaesthesia', 'Anaesthesia Record') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openNewCase()"><i class="mdi mdi-plus"></i> New Case</button>
          <button class="hms-btn hms-btn-blue" @click="loadCases"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.pre_op_count || 0 }}</div><div class="hms-stat-lbl">Pre-op</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.induction_count || 0 }}</div><div class="hms-stat-lbl">Induction</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.intra_op_count || 0 }}</div><div class="hms-stat-lbl">Intra-op</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.recovery_count || 0 }}</div><div class="hms-stat-lbl">Recovery</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.cases_today || 0 }}</div><div class="hms-stat-lbl">Cases today</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='active' }" @click="tab='active'"><i class="mdi mdi-format-list-bulleted me-1"></i>Active Cases</button>
        <button class="hms-tab" :class="{ active: tab==='record' }" @click="tab='record'" :disabled="!detail.case"><i class="mdi mdi-clipboard-text me-1"></i>Anaesthesia Chart</button>
        <button class="hms-tab" :class="{ active: tab==='history' }" @click="tab='history'; loadHistory()"><i class="mdi mdi-history me-1"></i>History</button>
      </div>

      <!-- ============ ACTIVE CASES ============ -->
      <div v-if="tab==='active'">
        <div v-if="!cases.length" class="hms-empty"><i class="mdi mdi-needle-syringe"></i><h5>No active anaesthesia cases</h5></div>
        <div v-else class="hms-case-grid">
          <div v-for="c in cases" :key="c.record_id" class="hms-case-card" :class="'st-' + c.status">
            <div class="hms-case-head">
              <div>
                <div class="hms-case-name">{{ c.patient_name }}</div>
                <div class="hms-muted-sm">{{ c.case_number }} · {{ c.patient_no }}</div>
              </div>
              <span class="hms-pill" :class="statusPill(c.status)">{{ formatStatus(c.status) }}</span>
            </div>
            <div class="hms-case-meta">
              <div><span class="hms-vk">Type</span> {{ formatAnesType(c.anaesthesia_type) }}</div>
              <div><span class="hms-vk">ASA</span> {{ c.asa_grade || '—' }}</div>
              <div><span class="hms-vk">Theatre</span> {{ c.theatre_name || '—' }}</div>
              <div v-if="c.minutes_since_induction != null"><span class="hms-vk">Elapsed</span> {{ formatMins(c.minutes_since_induction) }}</div>
            </div>
            <div v-if="c.planned_procedure" class="hms-case-procedure">{{ c.planned_procedure }}</div>
            <div class="hms-muted-sm" v-if="c.anaesthesiologist_name">Anaesthetist: {{ c.anaesthesiologist_name }}</div>
            <div class="hms-muted-sm" v-if="c.last_obs_at">Last obs: {{ formatRelative(c.last_obs_at) }} · {{ c.obs_count || 0 }} entries</div>
            <div class="hms-case-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openCase(c)"><i class="mdi mdi-folder-open"></i> Open chart</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ CHART (Pre-op + Intra-op + Recovery) ============ -->
      <div v-if="tab==='record' && detail.case">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.case.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.case.case_number }} · {{ detail.case.patient_no }} · {{ detail.case.sex }} · DOB {{ detail.case.date_of_birth }}</div>
                <div class="hms-muted-sm" v-if="detail.case.patient_allergies"><i class="mdi mdi-alert-octagon text-danger"></i> Allergies: {{ detail.case.patient_allergies }}</div>
              </div>
              <div class="text-end">
                <div><span class="hms-pill" :class="statusPill(detail.case.status)">{{ formatStatus(detail.case.status) }}</span> · ASA {{ detail.case.asa_grade }} · {{ formatAnesType(detail.case.anaesthesia_type) }}</div>
                <div class="hms-muted-sm" v-if="detail.case.theatre_name">{{ detail.case.theatre_name }}</div>
                <div class="hms-muted-sm" v-if="detail.case.anaesthesiologist_name">Anaesthetist: {{ detail.case.anaesthesiologist_name }}</div>
                <div class="hms-muted-sm" v-if="detail.case.surgeon_name">Surgeon: {{ detail.case.surgeon_name }}</div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openPreOpModal()"><i class="mdi mdi-clipboard-edit"></i> Edit Pre-op</button>
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openObsModal()"><i class="mdi mdi-pulse"></i> Add Vitals</button>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDrugModal()"><i class="mdi mdi-pill"></i> Drug / Fluid</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openEventModal()"><i class="mdi mdi-alert-circle"></i> Event</button>
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openRecoveryModal()" v-if="detail.case.status === 'intra_op' || detail.case.status === 'recovery'"><i class="mdi mdi-bed-outline"></i> Aldrete (PACU)</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="closeCaseDirect()" v-if="detail.case.status === 'recovery'"><i class="mdi mdi-flag-checkered"></i> Close Case</button>
            </div>
          </div>
        </div>

        <!-- INTRA-OP CHART -->
        <div class="hms-card mb-3" v-if="obsPoints.length">
          <div class="hms-card-head">Intra-operative chart</div>
          <div class="hms-card-body">
            <svg viewBox="0 0 900 360" style="width:100%; max-width:1100px; background:#fff;">
              <!-- HR shading band 60-100 -->
              <rect :x="60" :y="yHr(100)" :width="800" :height="yHr(60) - yHr(100)" fill="#f0fff4" />
              <!-- Y axis gridlines -->
              <g v-for="g in [40,60,80,100,120,140,160,180]" :key="'y'+g">
                <line :x1="60" :y1="yHr(g)" :x2="860" :y2="yHr(g)" stroke="#edf2f7" stroke-width="1" />
                <text :x="54" :y="yHr(g)+4" font-size="10" text-anchor="end" fill="#718096">{{ g }}</text>
              </g>
              <!-- X axis gridlines (every 30 minutes) -->
              <g v-for="m in xMinutesTicks" :key="'x'+m">
                <line :x1="xT(m)" :y1="20" :x2="xT(m)" :y2="320" stroke="#edf2f7" stroke-width="1" />
                <text :x="xT(m)" :y="338" font-size="10" text-anchor="middle" fill="#718096">{{ m }}m</text>
              </g>

              <!-- HR line + dots -->
              <polyline v-if="hrPoints.length > 1" :points="hrPoints.map(p => xT(p.t) + ',' + yHr(p.v)).join(' ')" fill="none" stroke="#2b6cb0" stroke-width="2" />
              <g v-for="(p, i) in hrPoints" :key="'hr'+i">
                <circle :cx="xT(p.t)" :cy="yHr(p.v)" r="3" fill="#2b6cb0" />
              </g>

              <!-- BP systolic/diastolic as ▲▼ marks -->
              <g v-for="(p, i) in bpPoints" :key="'bp'+i">
                <line :x1="xT(p.t)" :y1="yHr(p.dia)" :x2="xT(p.t)" :y2="yHr(p.sys)" stroke="#9b2c2c" stroke-width="1.5" />
                <polygon :points="(xT(p.t) - 4) + ',' + (yHr(p.sys) - 6) + ' ' + (xT(p.t) + 4) + ',' + (yHr(p.sys) - 6) + ' ' + xT(p.t) + ',' + (yHr(p.sys) - 1)" fill="#9b2c2c" />
                <polygon :points="(xT(p.t) - 4) + ',' + (yHr(p.dia) + 6) + ' ' + (xT(p.t) + 4) + ',' + (yHr(p.dia) + 6) + ' ' + xT(p.t) + ',' + (yHr(p.dia) + 1)" fill="#9b2c2c" />
              </g>

              <!-- SpO2 line (right axis) -->
              <polyline v-if="spo2Points.length > 1" :points="spo2Points.map(p => xT(p.t) + ',' + ySpo2(p.v)).join(' ')" fill="none" stroke="#16a34a" stroke-width="2" stroke-dasharray="4,2" />
              <g v-for="(p, i) in spo2Points" :key="'sp'+i">
                <circle :cx="xT(p.t)" :cy="ySpo2(p.v)" r="3" fill="#16a34a" />
              </g>

              <!-- Drug markers along bottom -->
              <g v-for="(d, i) in drugMarkers" :key="'dr'+i">
                <line :x1="xT(d.t)" :y1="20" :x2="xT(d.t)" :y2="320" stroke="#d69e2e" stroke-width="1" stroke-dasharray="2,3" opacity="0.5" />
                <text :x="xT(d.t)" :y="14" font-size="9" text-anchor="middle" fill="#7b341e">▼</text>
              </g>

              <!-- Event markers -->
              <g v-for="(e, i) in eventMarkers" :key="'ev'+i">
                <line :x1="xT(e.t)" :y1="20" :x2="xT(e.t)" :y2="320" stroke="#e53e3e" stroke-width="1.5" />
                <text :x="xT(e.t) + 4" :y="22 + (i % 3) * 12" font-size="9" fill="#742a2a">⚠ {{ e.label }}</text>
              </g>

              <!-- Y axis labels -->
              <text x="20" y="180" font-size="10" fill="#2b6cb0" transform="rotate(-90 20 180)">HR / BP</text>
              <text x="876" y="180" font-size="10" fill="#16a34a" transform="rotate(90 876 180)">SpO₂%</text>
            </svg>
            <div class="d-flex gap-3 mt-2 flex-wrap hms-muted-sm">
              <span><span class="hms-leg-line" style="background:#2b6cb0"></span>Heart rate</span>
              <span><span class="hms-leg-line" style="background:#9b2c2c"></span>BP (sys/dia)</span>
              <span><span class="hms-leg-line" style="background:#16a34a"></span>SpO₂</span>
              <span><span style="color:#7b341e">▼</span> Drug given</span>
              <span><span style="color:#742a2a">⚠</span> Event</span>
            </div>
          </div>
        </div>

        <!-- OBS / DRUG / EVENT TABLES side by side -->
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Observations ({{ detail.obs.length }})</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.obs.length" class="hms-table">
                <thead><tr><th>Time</th><th>HR</th><th>BP</th><th>SpO₂</th><th>EtCO₂</th><th>T°</th><th>Gas</th></tr></thead>
                <tbody>
                  <tr v-for="o in detail.obs" :key="o.obs_id">
                    <td>{{ formatTime(o.obs_at) }} <span class="hms-muted-sm">({{ o.minutes_from_start }}m)</span></td>
                    <td :class="hrClass(o.hr)">{{ o.hr || '—' }}</td>
                    <td>{{ o.bp_systolic || '—' }}/{{ o.bp_diastolic || '—' }}</td>
                    <td :class="spo2Class(o.spo2)">{{ o.spo2 || '—' }}</td>
                    <td>{{ o.etco2 || '—' }}</td>
                    <td>{{ o.temperature || '—' }}</td>
                    <td>{{ o.gas_agent ? (o.gas_agent + ' ' + (o.gas_pct || '') + '%') : '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No observations yet — click <strong>Add Vitals</strong>.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Drugs &amp; fluids ({{ detail.drugs.length }})</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.drugs.length" class="hms-table">
                <thead><tr><th>Time</th><th>Class</th><th>Drug</th><th>Dose</th><th>Route</th></tr></thead>
                <tbody>
                  <tr v-for="d in detail.drugs" :key="d.drug_id">
                    <td>{{ formatTime(d.given_at) }}</td>
                    <td><span class="hms-pill" :class="drugClassPill(d.drug_class)">{{ d.drug_class }}</span></td>
                    <td><strong>{{ d.drug_name }}</strong></td>
                    <td>{{ d.dose }} {{ d.dose_unit }}<span v-if="d.is_infusion == 1" class="hms-muted-sm"> ({{ d.infusion_rate || 'infusion' }})</span></td>
                    <td>{{ d.route }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No drugs given yet.</div>
            </div>
          </div>
        </div>

        <!-- EVENTS + RECOVERY -->
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Events ({{ detail.events.length }})</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.events.length" class="hms-table">
                <thead><tr><th>Time</th><th>Event</th><th>Severity</th><th>Action</th></tr></thead>
                <tbody>
                  <tr v-for="e in detail.events" :key="e.event_id" :class="severityClass(e.severity)">
                    <td>{{ formatTime(e.event_at) }}</td>
                    <td>{{ e.event_type.replace(/_/g, ' ') }}<div class="hms-muted-sm">{{ e.description }}</div></td>
                    <td><span class="hms-pill" :class="severityPill(e.severity)">{{ e.severity }}</span></td>
                    <td class="hms-muted-sm">{{ e.action_taken }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No events.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Recovery (Aldrete) — {{ detail.recovery.length }} assessments</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.recovery.length" class="hms-table">
                <thead><tr><th>Time</th><th>Aldrete</th><th>BP</th><th>HR</th><th>SpO₂</th><th>Pain</th><th>Disposition</th></tr></thead>
                <tbody>
                  <tr v-for="r in detail.recovery" :key="r.rec_id" :class="r.aldrete_score >= 9 ? 'row-success' : (r.aldrete_score < 7 ? 'row-action' : '')">
                    <td>{{ formatTime(r.assessed_at) }} <span class="hms-muted-sm">({{ r.minutes_in_pacu }}m)</span></td>
                    <td><strong :class="aldreteClass(r.aldrete_score)">{{ r.aldrete_score }}/10</strong></td>
                    <td>{{ r.bp_systolic || '—' }}/{{ r.bp_diastolic || '—' }}</td>
                    <td>{{ r.hr || '—' }}</td>
                    <td>{{ r.spo2 || '—' }}</td>
                    <td>{{ r.pain_score != null ? r.pain_score + '/10' : '—' }}</td>
                    <td>{{ r.discharge_to || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">Patient not yet in PACU.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ HISTORY ============ -->
      <div v-if="tab==='history'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="historyPeriod" class="hms-input" @change="loadHistory" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num">{{ historyStats.cases_total || 0 }}</div><div class="hms-stat-lbl">Closed cases</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ historyStats.general || 0 }}</div><div class="hms-stat-lbl">General</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ historyStats.regional || 0 }}</div><div class="hms-stat-lbl">Regional</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ historyStats.high_asa || 0 }}</div><div class="hms-stat-lbl">High ASA (≥3)</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ historyStats.avg_duration_min || 0 }}m</div><div class="hms-stat-lbl">Avg duration</div></div>
        </div>
        <table class="hms-table">
          <thead><tr><th>Case #</th><th>Date</th><th>Patient</th><th>Type</th><th>ASA</th><th>Duration</th><th>Disposition</th><th>Anaesthetist</th></tr></thead>
          <tbody>
            <tr v-for="c in historyCases" :key="c.record_id" :class="c.final_disposition === 'died' ? 'row-action' : ''">
              <td><strong>{{ c.case_number }}</strong></td>
              <td>{{ c.case_date }}</td>
              <td>{{ c.patient_name }}</td>
              <td>{{ formatAnesType(c.anaesthesia_type) }}</td>
              <td>{{ c.asa_grade }}</td>
              <td>{{ c.duration_min ? c.duration_min + 'm' : '—' }}</td>
              <td><span class="hms-pill" :class="dispoPill(c.final_disposition)">{{ c.final_disposition || '—' }}</span></td>
              <td>{{ c.anaesthesiologist_name || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== MODALS ===== -->
    <div v-if="newCaseModal" class="hms-modal-bg" @click.self="newCaseModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>New Anaesthesia Case</h5><button class="hms-icon-btn" @click="newCaseModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!newCase.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="newCase.patient_search" class="hms-input" placeholder="Search by patient # / name" @keyup.enter="findPatientForCase" />
              <button class="hms-btn hms-btn-blue" @click="findPatientForCase">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ newCase.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="newCase.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Case date *</label><input v-model="newCase.case_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Anaesthesia type</label>
              <select v-model="newCase.anaesthesia_type" class="hms-input">
                <option value="general">General</option><option value="spinal">Spinal</option><option value="epidural">Epidural</option>
                <option value="combined_spinal_epidural">CSE</option><option value="regional">Regional block</option>
                <option value="local">Local</option><option value="sedation">Sedation</option><option value="MAC">MAC</option><option value="TIVA">TIVA</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">ASA grade</label>
              <select v-model="newCase.asa_grade" class="hms-input">
                <option value="1">I — healthy</option><option value="2">II — mild systemic</option>
                <option value="3">III — severe systemic</option><option value="4">IV — life-threatening</option>
                <option value="5">V — moribund</option><option value="1E">I-E</option><option value="2E">II-E</option>
                <option value="3E">III-E</option><option value="4E">IV-E</option><option value="5E">V-E</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Planned procedure *</label><input v-model="newCase.planned_procedure" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Mallampati</label>
              <select v-model="newCase.mallampati" class="hms-input"><option value="">—</option><option value="1">I</option><option value="2">II</option><option value="3">III</option><option value="4">IV</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Fasting solid (hr)</label><input v-model.number="newCase.fasting_solid_hrs" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Fasting liquid (hr)</label><input v-model.number="newCase.fasting_liquid_hrs" type="number" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="newCase.consent_obtained" :true-value="1" :false-value="0" /> Consent obtained</label></div>
            <div class="col-12"><label class="hms-label">Allergies</label><input v-model="newCase.allergies" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Comorbidities</label><input v-model="newCase.comorbidities" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Airway notes</label><input v-model="newCase.airway_notes" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Induction at</label><input v-model="newCase.induction_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Pre-op assessed at</label><input v-model="newCase.pre_op_assessed_at" type="datetime-local" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="newCaseModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !newCase.patient_id || !newCase.planned_procedure" @click="saveNewCase">{{ saving?'Saving…':'Start Case' }}</button>
        </div>
      </div>
    </div>

    <!-- VITALS MODAL -->
    <div v-if="obsModal" class="hms-modal-bg" @click.self="obsModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Add intra-op observation</h5><button class="hms-icon-btn" @click="obsModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">At *</label><input v-model="obs.obs_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">HR</label><input v-model.number="obs.hr" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">SpO₂%</label><input v-model.number="obs.spo2" type="number" min="0" max="100" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Sys</label><input v-model.number="obs.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Dia</label><input v-model.number="obs.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">EtCO₂</label><input v-model.number="obs.etco2" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">RR</label><input v-model.number="obs.respiratory_rate" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Temp °C</label><input v-model.number="obs.temperature" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">FiO₂%</label><input v-model.number="obs.fio2_pct" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Gas</label>
              <select v-model="obs.gas_agent" class="hms-input"><option value="">—</option><option value="sevoflurane">Sevo</option><option value="isoflurane">Iso</option><option value="desflurane">Des</option><option value="halothane">Halo</option><option value="none">none</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Gas %</label><input v-model.number="obs.gas_pct" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">N₂O %</label><input v-model.number="obs.n2o_pct" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BIS</label><input v-model.number="obs.bis_score" type="number" min="0" max="100" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Urine ml</label><input v-model.number="obs.urine_output_ml" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><input v-model="obs.notes" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="obsModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveObs">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- DRUG MODAL -->
    <div v-if="drugModal" class="hms-modal-bg" @click.self="drugModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Drug / Fluid given</h5><button class="hms-icon-btn" @click="drugModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">At *</label><input v-model="drug.given_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Class</label>
              <select v-model="drug.drug_class" class="hms-input">
                <option value="induction">Induction</option><option value="maintenance">Maintenance</option>
                <option value="muscle_relaxant">Muscle relaxant</option><option value="reversal">Reversal</option>
                <option value="opioid">Opioid</option><option value="antibiotic">Antibiotic</option>
                <option value="fluid">IV Fluid</option><option value="blood">Blood</option>
                <option value="vasopressor">Vasopressor</option><option value="antiemetic">Antiemetic</option>
                <option value="antihypertensive">Antihypertensive</option><option value="local">Local anaesthetic</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Drug / fluid name *</label><input v-model="drug.drug_name" class="hms-input" placeholder="e.g. Propofol / Fentanyl / Normal saline" /></div>
            <div class="col-4"><label class="hms-label">Dose</label><input v-model.number="drug.dose" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Unit</label>
              <select v-model="drug.dose_unit" class="hms-input"><option value="mg">mg</option><option value="mcg">mcg</option><option value="ml">ml</option><option value="L">L</option><option value="U">Units</option><option value="IU">IU</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Route</label>
              <select v-model="drug.route" class="hms-input"><option value="IV">IV</option><option value="IM">IM</option><option value="SC">SC</option><option value="PO">PO</option><option value="inhaled">Inhaled</option><option value="intrathecal">Intrathecal</option><option value="epidural">Epidural</option><option value="topical">Topical</option><option value="infusion">Infusion</option></select>
            </div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="drug.is_infusion" :true-value="1" :false-value="0" /> Infusion</label></div>
            <div class="col-8" v-if="drug.is_infusion == 1"><label class="hms-label">Infusion rate</label><input v-model="drug.infusion_rate" class="hms-input" placeholder="e.g. 5 mcg/kg/min" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><input v-model="drug.notes" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="drugModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !drug.drug_name" @click="saveDrug">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- EVENT MODAL -->
    <div v-if="eventModal" class="hms-modal-bg" @click.self="eventModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Record event</h5><button class="hms-icon-btn" @click="eventModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">At *</label><input v-model="evt.event_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Type</label>
              <select v-model="evt.event_type" class="hms-input">
                <option value="intubation">Intubation</option><option value="extubation">Extubation</option>
                <option value="LMA_insertion">LMA insertion</option><option value="spinal_block">Spinal block</option>
                <option value="epidural_insertion">Epidural insertion</option>
                <option value="desaturation">Desaturation</option><option value="hypotension">Hypotension</option>
                <option value="hypertension">Hypertension</option><option value="bradycardia">Bradycardia</option>
                <option value="tachycardia">Tachycardia</option><option value="arrhythmia">Arrhythmia</option>
                <option value="laryngospasm">Laryngospasm</option><option value="bronchospasm">Bronchospasm</option>
                <option value="blood_loss">Blood loss</option><option value="transfusion">Transfusion</option>
                <option value="vomiting">Vomiting</option><option value="awareness">Awareness</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Severity</label>
              <select v-model="evt.severity" class="hms-input"><option value="minor">Minor</option><option value="moderate">Moderate</option><option value="severe">Severe</option><option value="critical">Critical</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Description</label><textarea v-model="evt.description" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Action taken</label><textarea v-model="evt.action_taken" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="eventModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveEvent">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- RECOVERY (ALDRETE) MODAL -->
    <div v-if="recoveryModal" class="hms-modal-bg" @click.self="recoveryModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Aldrete recovery score</h5><button class="hms-icon-btn" @click="recoveryModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">Score each parameter 0-2. Total ≥9 + stable vitals → ready for ward discharge.</div>
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Assessed at *</label><input v-model="recovery.assessed_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"></div>
            <div class="col-12"><label class="hms-label">Activity (limbs)</label>
              <select v-model.number="recovery.activity" class="hms-input">
                <option :value="2">2 — Moves all 4 limbs voluntarily</option>
                <option :value="1">1 — Moves 2 limbs</option>
                <option :value="0">0 — Cannot move limbs</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Respiration</label>
              <select v-model.number="recovery.respiration" class="hms-input">
                <option :value="2">2 — Breathes deeply &amp; coughs freely</option>
                <option :value="1">1 — Dyspnoea / limited breathing</option>
                <option :value="0">0 — Apnoeic</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Circulation</label>
              <select v-model.number="recovery.circulation" class="hms-input">
                <option :value="2">2 — BP ±20% of pre-anaesthetic</option>
                <option :value="1">1 — BP ±20-50% of pre-anaesthetic</option>
                <option :value="0">0 — BP &gt;50% off pre-anaesthetic</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Consciousness</label>
              <select v-model.number="recovery.consciousness" class="hms-input">
                <option :value="2">2 — Fully awake</option>
                <option :value="1">1 — Arousable on calling</option>
                <option :value="0">0 — Not responding</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Color / O₂ saturation</label>
              <select v-model.number="recovery.color_o2sat" class="hms-input">
                <option :value="2">2 — SpO₂ &gt;92% on room air</option>
                <option :value="1">1 — Needs O₂ to maintain &gt;90%</option>
                <option :value="0">0 — SpO₂ &lt;90% even with O₂</option>
              </select>
            </div>
            <div class="col-12 hms-aldrete-total" :class="aldreteClass(aldretePreviewScore)">
              Aldrete total: <strong>{{ aldretePreviewScore }}/10</strong>
              <span v-if="aldretePreviewScore >= 9"> — ready for discharge</span>
              <span v-else-if="aldretePreviewScore < 7"> — stay in PACU</span>
              <span v-else> — borderline, reassess in 15 min</span>
            </div>
            <div class="col-3"><label class="hms-label">BP Sys</label><input v-model.number="recovery.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Dia</label><input v-model.number="recovery.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">HR</label><input v-model.number="recovery.hr" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">SpO₂</label><input v-model.number="recovery.spo2" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Pain</label><input v-model.number="recovery.pain_score" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Discharge to (only if score ≥9)</label>
              <select v-model="recovery.discharge_to" class="hms-input"><option value="">— stay —</option><option value="ward">Ward</option><option value="hdu">HDU</option><option value="icu">ICU</option><option value="home">Home</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Notes</label><input v-model="recovery.notes" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="recoveryModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveRecovery">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "AnaesthesiaRecord",
  components: { Navigation },
  data() {
    return {
      tab: 'active', saving: false,
      stats: {}, cases: [],
      detail: { case: null, obs: [], drugs: [], events: [], recovery: [] },

      historyPeriod: 'this_month',
      historyCases: [], historyStats: {},

      newCaseModal: false, newCase: this.blankNewCase(),
      obsModal: false, obs: this.blankObs(),
      drugModal: false, drug: this.blankDrug(),
      eventModal: false, evt: this.blankEvent(),
      recoveryModal: false, recovery: this.blankRecovery()
    };
  },
  computed: {
    obsPoints() { return (this.detail.obs || []).filter(o => o.minutes_from_start != null); },
    hrPoints()  { return this.obsPoints.filter(o => o.hr).map(o => ({ t: o.minutes_from_start, v: parseInt(o.hr) })); },
    spo2Points(){ return this.obsPoints.filter(o => o.spo2).map(o => ({ t: o.minutes_from_start, v: parseInt(o.spo2) })); },
    bpPoints()  { return this.obsPoints.filter(o => o.bp_systolic && o.bp_diastolic).map(o => ({ t: o.minutes_from_start, sys: parseInt(o.bp_systolic), dia: parseInt(o.bp_diastolic) })); },
    drugMarkers() {
      const start = this.detail.case && this.detail.case.induction_at ? new Date(this.detail.case.induction_at.replace(' ','T')).getTime() : null;
      if (!start) return [];
      return (this.detail.drugs || []).map(d => ({
        t: Math.floor((new Date(d.given_at.replace(' ','T')).getTime() - start) / 60000),
        label: d.drug_name
      })).filter(d => d.t >= 0 && d.t <= this.maxXMinutes);
    },
    eventMarkers() {
      const start = this.detail.case && this.detail.case.induction_at ? new Date(this.detail.case.induction_at.replace(' ','T')).getTime() : null;
      if (!start) return [];
      return (this.detail.events || []).map(e => ({
        t: Math.floor((new Date(e.event_at.replace(' ','T')).getTime() - start) / 60000),
        label: e.event_type.replace(/_/g, ' ')
      })).filter(e => e.t >= 0 && e.t <= this.maxXMinutes);
    },
    maxXMinutes() {
      const all = [...this.hrPoints, ...this.spo2Points, ...this.bpPoints].map(p => p.t);
      const m = all.length ? Math.max(...all) : 60;
      return Math.max(60, Math.ceil(m / 30) * 30);
    },
    xMinutesTicks() {
      const ticks = [];
      for (let m = 0; m <= this.maxXMinutes; m += 30) ticks.push(m);
      return ticks;
    },
    aldretePreviewScore() {
      return ['activity','respiration','circulation','consciousness','color_o2sat']
        .reduce((s, k) => s + (this.recovery[k] != null ? parseInt(this.recovery[k]) : 0), 0);
    }
  },
  mounted() { this.loadCases(); },
  methods: {
    blankNewCase() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return {
        patient_id: null, patient_name: '', patient_search: '',
        case_date: now.toISOString().substring(0,10),
        anaesthesia_type: 'general', asa_grade: '2', mallampati: '',
        planned_procedure: '',
        fasting_solid_hrs: null, fasting_liquid_hrs: null, consent_obtained: 1,
        allergies: '', comorbidities: '', airway_notes: '',
        induction_at: localDt, pre_op_assessed_at: localDt
      };
    },
    blankObs() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return { obs_at: localDt, hr: null, bp_systolic: null, bp_diastolic: null, spo2: null, etco2: null, respiratory_rate: null, temperature: null, fio2_pct: null, gas_agent: '', gas_pct: null, n2o_pct: null, bis_score: null, urine_output_ml: null, notes: '' };
    },
    blankDrug() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return { given_at: localDt, drug_class: 'induction', drug_name: '', dose: null, dose_unit: 'mg', route: 'IV', is_infusion: 0, infusion_rate: '', notes: '' };
    },
    blankEvent() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return { event_at: localDt, event_type: 'desaturation', severity: 'minor', description: '', action_taken: '' };
    },
    blankRecovery() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,16);
      return { assessed_at: localDt, activity: 2, respiration: 2, circulation: 2, consciousness: 2, color_o2sat: 2, bp_systolic: null, bp_diastolic: null, hr: null, spo2: null, pain_score: 0, discharge_to: '', notes: '' };
    },

    async loadCases() {
      try {
        const r = await executeJson({ function: 'getActiveAnaesthesiaCases' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.cases = d.Cases || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadHistory() {
      try {
        const r = await executeJson({ function: 'getAnaesthesiaHistory', period: this.historyPeriod }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.historyCases = d.Cases || [];
          this.historyStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openNewCase() { this.newCase = this.blankNewCase(); this.newCaseModal = true; },
    async findPatientForCase() {
      const q = (this.newCase.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.newCase.patient_id = p.patient_id;
        this.newCase.patient_name = p.first_name + ' ' + p.last_name;
        if (p.allergies) this.newCase.allergies = p.allergies;
        if (p.chronic_conditions) this.newCase.comorbidities = p.chronic_conditions;
      }
    },
    async saveNewCase() {
      this.saving = true;
      try {
        const params = { function: 'saveAnaesthesiaRecord', ...this.newCase, status: 'pre_op' };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) {
          this.newCaseModal = false;
          await this.loadCases();
          // open the new case immediately
          await this.openCaseById(r.data.data.record_id);
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openCase(c) { await this.openCaseById(c.record_id); },
    async openCaseById(rid) {
      try {
        const r = await executeJson({ function: 'getAnaesthesiaDetail', record_id: rid }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail = { case: d.Case, obs: d.Obs || [], drugs: d.Drugs || [], events: d.Events || [], recovery: d.Recovery || [] };
          this.tab = 'record';
        }
      } catch (e) { console.error(e); }
    },

    openPreOpModal() {
      this.newCase = { ...this.blankNewCase(), ...this.detail.case, patient_search: '', patient_name: this.detail.case.patient_name };
      this.newCaseModal = true;
    },
    openObsModal()      { this.obs      = this.blankObs();      this.obsModal = true; },
    openDrugModal()     { this.drug     = this.blankDrug();     this.drugModal = true; },
    openEventModal()    { this.evt      = this.blankEvent();    this.eventModal = true; },
    openRecoveryModal() {
      this.recovery = this.blankRecovery();
      // Auto-set recovery_in_at if not yet
      if (this.detail.case && !this.detail.case.recovery_in_at) {
        this.markRecoveryStart();
      }
      this.recoveryModal = true;
    },
    async markRecoveryStart() {
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,19).replace('T', ' ');
      await executeJson({ function: 'saveAnaesthesiaRecord', record_id: this.detail.case.record_id, recovery_in_at: localDt, status: 'recovery' }, hms);
    },

    async saveObs() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordAnaesthesiaObs', record_id: this.detail.case.record_id, ...this.obs }, hms);
        if (r.data && r.data.success) { this.obsModal = false; await this.openCaseById(this.detail.case.record_id); await this.loadCases(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async saveDrug() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordAnaesthesiaDrug', record_id: this.detail.case.record_id, ...this.drug }, hms);
        if (r.data && r.data.success) { this.drugModal = false; await this.openCaseById(this.detail.case.record_id); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async saveEvent() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordAnaesthesiaEvent', record_id: this.detail.case.record_id, ...this.evt }, hms);
        if (r.data && r.data.success) { this.eventModal = false; await this.openCaseById(this.detail.case.record_id); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async saveRecovery() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordAnaesthesiaRecovery', record_id: this.detail.case.record_id, ...this.recovery }, hms);
        if (r.data && r.data.success) { this.recoveryModal = false; await this.openCaseById(this.detail.case.record_id); await this.loadCases(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async closeCaseDirect() {
      const dispo = prompt('Final disposition (ward / hdu / icu / home / died)?', 'ward');
      if (!dispo) return;
      const now = new Date();
      const localDt = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0,19).replace('T', ' ');
      await executeJson({ function: 'saveAnaesthesiaRecord', record_id: this.detail.case.record_id, status: 'closed', recovery_out_at: localDt, final_disposition: dispo }, hms);
      await this.openCaseById(this.detail.case.record_id);
      await this.loadCases();
    },

    // ===== chart helpers =====
    xT(m) { return 60 + (m / Math.max(1, this.maxXMinutes)) * 800; },
    yHr(v) { return 320 - ((v - 30) / 170) * 300; },                 // 30..200 range
    ySpo2(v) { return 320 - ((v - 70) / 30) * 300; },                // 70..100 range

    // ===== formatting =====
    formatStatus(s) { return ({ pre_op: 'Pre-op', induction: 'Induction', intra_op: 'Intra-op', recovery: 'Recovery', closed: 'Closed', cancelled: 'Cancelled' })[s] || s; },
    statusPill(s)   { return ({ pre_op: 'hms-pill-info', induction: 'hms-pill-warn', intra_op: 'hms-pill-danger', recovery: 'hms-pill-warn', closed: 'hms-pill-success', cancelled: 'hms-pill-muted' })[s] || ''; },
    formatAnesType(t) { return ({ general: 'General', regional: 'Regional', spinal: 'Spinal', epidural: 'Epidural', combined_spinal_epidural: 'CSE', local: 'Local', sedation: 'Sedation', MAC: 'MAC', TIVA: 'TIVA' })[t] || t; },
    drugClassPill(c) { return ({ induction: 'hms-pill-info', maintenance: 'hms-pill-info', muscle_relaxant: 'hms-pill-warn', reversal: 'hms-pill-success', opioid: 'hms-pill-warn', antibiotic: 'hms-pill-info', fluid: 'hms-pill-muted', blood: 'hms-pill-danger', vasopressor: 'hms-pill-warn', antiemetic: 'hms-pill-muted', antihypertensive: 'hms-pill-info', local: 'hms-pill-info', other: 'hms-pill-muted' })[c] || 'hms-pill-muted'; },
    severityPill(s) { return ({ minor: 'hms-pill-muted', moderate: 'hms-pill-warn', severe: 'hms-pill-danger', critical: 'hms-pill-danger' })[s] || ''; },
    severityClass(s) { return s === 'severe' || s === 'critical' ? 'row-action' : (s === 'moderate' ? 'row-warn' : ''); },
    dispoPill(d) { return ({ ward: 'hms-pill-success', hdu: 'hms-pill-warn', icu: 'hms-pill-danger', home: 'hms-pill-success', died: 'hms-pill-danger' })[d] || 'hms-pill-muted'; },
    hrClass(v) { if (!v) return ''; if (v < 50 || v > 110) return 'st-bad'; return ''; },
    spo2Class(v) { if (!v) return ''; if (v < 92) return 'st-bad'; return ''; },
    aldreteClass(s) { if (s == null) return ''; if (s >= 9) return 'st-good'; if (s < 7) return 'st-bad'; return 'st-warn'; },
    formatTime(s) { if (!s) return '—'; const d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); },
    formatRelative(s) {
      if (!s) return '—';
      const d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return s;
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return mins + 'm ago';
      const h = Math.floor(mins / 60); if (h < 24) return h + 'h ago';
      return Math.floor(h / 24) + 'd ago';
    },
    formatMins(m) { if (m == null) return '—'; const h = Math.floor(m / 60); return h > 0 ? (h + 'h ' + (m - h * 60) + 'm') : (m + 'm'); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; } .hms-stat-success { color: #276749; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-case-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
.hms-case-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; }
.hms-case-card.st-pre_op    { border-left-color: #4299e1; }
.hms-case-card.st-induction { border-left-color: #d69e2e; background: #fffaf0; }
.hms-case-card.st-intra_op  { border-left-color: #e53e3e; background: #fff5f5; }
.hms-case-card.st-recovery  { border-left-color: #d69e2e; }
.hms-case-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; margin-bottom: 8px; }
.hms-case-name { font-weight: 600; color: #1a202c; }
.hms-case-meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px; font-size: 12px; margin-bottom: 6px; }
.hms-vk { display: inline-block; min-width: 60px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }
.hms-case-procedure { font-size: 13px; color: #4a5568; padding: 6px 8px; background: #f7fafc; border-radius: 4px; margin-bottom: 6px; }
.hms-case-actions { margin-top: 8px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 6px 8px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn    { background: #fffaf0; }
.hms-table tr.row-action  { background: #fff5f5; }
.hms-table tr.row-success { background: #f0fff4; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #a0aec0; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-aldrete-total { padding: 10px 14px; border-radius: 6px; font-size: 14px; text-align: center; }
.hms-aldrete-total.st-good { background: #c6f6d5; color: #22543d; }
.hms-aldrete-total.st-warn { background: #feebc8; color: #7b341e; }
.hms-aldrete-total.st-bad  { background: #fed7d7; color: #742a2a; }

.hms-leg-line { display: inline-block; width: 18px; height: 3px; vertical-align: middle; margin-right: 4px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.text-danger { color: #c53030; }
.st-bad { color: #c53030; font-weight: 600; }
.st-good { color: #276749; font-weight: 700; }
.st-warn { color: #b45309; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
